
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {IdempotencyInfoUpdateResponse} from './IdempotencyInfoUpdateResponse';
import {IdempotentRequestState} from '../storage/IdempotentRequestState';
import {FieldModelIdempotentRequestState} from '../storage/IdempotentRequestState';

/**
 * IdempotencyInfoUpdateRequest struct
 */
class IdempotencyInfoUpdateRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!string=} hash
   * @param {Uint8Array=} responseBlob
   * @param {!IdempotentRequestState=} state
   * @param {!Date=} updated
   * @constructor
   */
  constructor (parent = new Request(), hash = '', responseBlob = null, state = new IdempotentRequestState(), updated = new Date(0)) {
    super()
    super.copy(parent)
    this.Hash = hash
    this.ResponseBlob = responseBlob
    this.State = state
    this.Updated = updated
  }

  /**
   * Copy struct (shallow copy)
   * @this {!IdempotencyInfoUpdateRequest}
   * @param {!IdempotencyInfoUpdateRequest} other Other struct
   * @returns {!IdempotencyInfoUpdateRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Hash != null) {
      this.Hash = other.Hash
    } else {
      this.Hash = null
    }
    if (other.ResponseBlob != null) {
      if (typeof other.ResponseBlob === 'string') {
        // noinspection JSUnresolvedFunction
        this.ResponseBlob = Uint8Array.from(Buffer.from(other.ResponseBlob, 'base64'))
      } else {
        this.ResponseBlob = Uint8Array.from(other.ResponseBlob)
      }
    } else {
      this.ResponseBlob = null
    }
    if (other.State != null) {
      this.State = IdempotentRequestState.fromObject(other.State)
    } else {
      this.State = null
    }
    if (other.Updated != null) {
      if (other.Updated instanceof Date) {
        this.Updated = new Date(other.Updated.getTime())
      } else {
        this.Updated = new Date(Math.round(other.Updated / 1000000))
      }
    } else {
      this.Updated = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!IdempotencyInfoUpdateRequest}
   * @returns {!IdempotencyInfoUpdateRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new IdempotencyInfoUpdateRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new IdempotencyInfoUpdateRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!IdempotencyInfoUpdateRequest}
   * @param {!IdempotencyInfoUpdateRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof IdempotencyInfoUpdateRequest)) {
      throw new TypeError('Instance of IdempotencyInfoUpdateRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!IdempotencyInfoUpdateRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Hash: ((this.Hash != null) ? this.Hash : null),
      ResponseBlob: ((this.ResponseBlob != null) ? Buffer.from(this.ResponseBlob).toString('base64') : null),
      State: ((this.State != null) ? this.State : null),
      Updated: ((this.Updated != null) ? (this.Updated.getTime() * 1000000) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return IdempotencyInfoUpdateRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!IdempotencyInfoUpdateRequest} other Object value
   * @returns {!IdempotencyInfoUpdateRequest} Created struct
   */
  static fromObject (other) {
    return new IdempotencyInfoUpdateRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!IdempotencyInfoUpdateRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return IdempotencyInfoUpdateRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!IdempotencyInfoUpdateRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4225
  }
}

export { IdempotencyInfoUpdateRequest };

/**
 * Fast Binary Encoding IdempotencyInfoUpdateRequest field model
 */
class FieldModelIdempotencyInfoUpdateRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.Hash = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.ResponseBlob = new fbe.FieldModelOptional(new fbe.FieldModelBytes(buffer, this.Hash.fbeOffset + this.Hash.fbeSize), buffer, this.Hash.fbeOffset + this.Hash.fbeSize)
    this.State = new FieldModelIdempotentRequestState(buffer, this.ResponseBlob.fbeOffset + this.ResponseBlob.fbeSize)
    this.Updated = new fbe.FieldModelTimestamp(buffer, this.State.fbeOffset + this.State.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Hash.fbeSize + this.ResponseBlob.fbeSize + this.State.fbeSize + this.Updated.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Hash.fbeExtra + this.ResponseBlob.fbeExtra + this.State.fbeExtra + this.Updated.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelIdempotencyInfoUpdateRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4225
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Hash.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Hash.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Hash.fbeSize

    if ((fbeCurrentSize + this.ResponseBlob.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ResponseBlob.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResponseBlob.fbeSize

    if ((fbeCurrentSize + this.State.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.State.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.State.fbeSize

    if ((fbeCurrentSize + this.Updated.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Updated.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Updated.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {!IdempotencyInfoUpdateRequest} fbeValue Default value, defaults is new IdempotencyInfoUpdateRequest()
   * @returns {!IdempotencyInfoUpdateRequest} IdempotencyInfoUpdateRequest value
   */
  get (fbeValue = new IdempotencyInfoUpdateRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {!IdempotencyInfoUpdateRequest} fbeValue IdempotencyInfoUpdateRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Hash.fbeSize) <= fbeStructSize) {
      fbeValue.Hash = this.Hash.get()
    } else {
      fbeValue.Hash = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Hash.fbeSize

    if ((fbeCurrentSize + this.ResponseBlob.fbeSize) <= fbeStructSize) {
      fbeValue.ResponseBlob = this.ResponseBlob.get()
    } else {
      fbeValue.ResponseBlob = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResponseBlob.fbeSize

    if ((fbeCurrentSize + this.State.fbeSize) <= fbeStructSize) {
      fbeValue.State = this.State.get()
    } else {
      fbeValue.State = new IdempotentRequestState()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.State.fbeSize

    if ((fbeCurrentSize + this.Updated.fbeSize) <= fbeStructSize) {
      fbeValue.Updated = this.Updated.get()
    } else {
      fbeValue.Updated = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Updated.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {!IdempotencyInfoUpdateRequest} fbeValue IdempotencyInfoUpdateRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelIdempotencyInfoUpdateRequest}
   * @param {IdempotencyInfoUpdateRequest} fbeValue IdempotencyInfoUpdateRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Hash.set(fbeValue.Hash)
    this.ResponseBlob.set(fbeValue.ResponseBlob)
    this.State.set(fbeValue.State)
    this.Updated.set(fbeValue.Updated)
  }
}

export { FieldModelIdempotencyInfoUpdateRequest };

/**
 * Fast Binary Encoding IdempotencyInfoUpdateRequest model
 */
class IdempotencyInfoUpdateRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelIdempotencyInfoUpdateRequest(this.buffer, 4)
  }

  /**
   * Get the IdempotencyInfoUpdateRequest model
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @returns {!FieldModelIdempotencyInfoUpdateRequest} model IdempotencyInfoUpdateRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return IdempotencyInfoUpdateRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelIdempotencyInfoUpdateRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @param {!IdempotencyInfoUpdateRequest} value IdempotencyInfoUpdateRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @param {!IdempotencyInfoUpdateRequest} value IdempotencyInfoUpdateRequest value, defaults is new IdempotencyInfoUpdateRequest()
   * @return {!object} Deserialized IdempotencyInfoUpdateRequest value and its size
   */
  deserialize (value = new IdempotencyInfoUpdateRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new IdempotencyInfoUpdateRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new IdempotencyInfoUpdateRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!IdempotencyInfoUpdateRequestModel}
   * @param {!number} prev Previous IdempotencyInfoUpdateRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { IdempotencyInfoUpdateRequestModel };
IdempotencyInfoUpdateRequest.__has_response = true;
IdempotencyInfoUpdateRequest.__response_class = IdempotencyInfoUpdateResponse;
